import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import {
  ShieldStar, Copy, Check, Warning, Lock, LockOpen, Sparkle,
} from "@phosphor-icons/react";

// Nice OTP-style input (6 digits)
function OtpInput({ value, onChange, autoFocus, id }) {
  return (
    <input id={id}
      inputMode="numeric" pattern="[0-9]*" maxLength={6}
      value={value}
      onChange={(e) => onChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
      autoFocus={autoFocus}
      data-testid="2fa-code-input"
      placeholder="000000"
      className="w-40 tracking-[0.4em] text-center text-lg font-mono bg-background-deep border border-border rounded px-3 py-2.5 text-white focus:outline-none focus:border-brand-cyan transition-colors" />
  );
}

export default function TwoFactorCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [busy, setBusy] = useState(false);
  const [setupData, setSetupData] = useState(null);   // { secret, otpauth_uri, qr_png_b64 }
  const [enrollCode, setEnrollCode] = useState("");
  const [backupCodes, setBackupCodes] = useState(null);    // list<string> shown once

  const [showDisable, setShowDisable] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState("");
  const [secretVisible, setSecretVisible] = useState(false);
  const [copiedIdx, setCopiedIdx] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/auth/2fa/status");
      setStatus(data);
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startSetup = async () => {
    setError("");
    setBackupCodes(null);
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/setup");
      setSetupData(data);
      setEnrollCode("");
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const enable = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/enable", { code: enrollCode });
      setBackupCodes(data.backup_codes);
      setSetupData(null);
      setEnrollCode("");
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const disable = async () => {
    setError("");
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { code: disableCode });
      setShowDisable(false);
      setDisableCode("");
      setBackupCodes(null);
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const regenerate = async () => {
    setError("");
    setBusy(true);
    try {
      const { data } = await api.post("/auth/2fa/regenerate-backup-codes", { code: regenCode });
      setBackupCodes(data.backup_codes);
      setShowRegen(false);
      setRegenCode("");
      await load();
    } catch (e) {
      setError(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  const copyCode = (code, i) => {
    navigator.clipboard.writeText(code);
    setCopiedIdx(i);
    setTimeout(() => setCopiedIdx(null), 1200);
  };

  const enabled = !!status?.enabled;

  return (
    <section className="sw-card overflow-hidden" data-testid="two-factor-card">
      <div className="px-6 py-4 border-b border-border flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-md flex items-center justify-center ${
            enabled ? "bg-emerald-500/15 text-emerald-400" : "bg-brand-cyan/15 text-brand-cyan"
          }`}>
            <ShieldStar size={18} weight="duotone" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-white tracking-tight">
                Two-factor authentication
              </h2>
              <span data-testid="2fa-status-pill"
                className={`inline-flex items-center px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.15em] border rounded ${
                  enabled
                    ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-300"
                    : "border-slate-500/40 bg-slate-500/10 text-slate-300"
                }`}>
                {enabled ? "Enabled" : "Off"}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-0.5">
              Adds an authenticator-app code to your magic-link sign-in.
            </p>
          </div>
        </div>
        {enabled && (
          <button onClick={() => setShowDisable(!showDisable)}
            data-testid="2fa-disable-toggle"
            className="text-[11px] font-semibold uppercase tracking-[0.15em] border border-red-500/40 text-red-300 hover:bg-red-500/10 rounded px-3 py-1.5 transition-colors">
            <LockOpen size={11} weight="bold" className="inline mr-1" />
            Disable 2FA
          </button>
        )}
      </div>

      <div className="p-6 space-y-5">
        {error && (
          <div className="text-xs text-red-300 border border-red-500/30 bg-red-500/10 rounded px-3 py-2"
            data-testid="2fa-error">
            {error}
          </div>
        )}

        {loading ? (
          <div className="text-sm text-slate-500 text-center py-4">Loading…</div>
        ) : !enabled ? (
          !setupData ? (
            <div className="flex items-start justify-between gap-4">
              <div className="text-sm text-slate-400 leading-relaxed max-w-2xl">
                Protect this admin account with an authenticator app (Google Authenticator, Authy,
                1Password, Microsoft Authenticator). We&apos;ll show a QR code, verify your first
                6-digit code, and issue 10 one-time backup codes.
              </div>
              <button onClick={startSetup} disabled={busy}
                data-testid="2fa-setup-btn"
                className="shrink-0 inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold">
                <Sparkle size={14} weight="bold" />
                {busy ? "…" : "Set up 2FA"}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-[280px_minmax(0,1fr)] gap-6">
              <div>
                <img
                  src={`data:image/png;base64,${setupData.qr_png_b64}`}
                  alt="Authenticator QR code"
                  className="w-full h-auto rounded-lg bg-white p-3"
                  data-testid="2fa-qr-image" />
                <button onClick={() => setSecretVisible(!secretVisible)}
                  data-testid="2fa-secret-toggle"
                  className="mt-2 text-[11px] text-slate-500 hover:text-brand-cyan transition-colors">
                  {secretVisible ? "Hide" : "Show"} secret (for manual entry)
                </button>
                {secretVisible && (
                  <div className="mt-1 text-[11px] font-mono text-slate-300 break-all bg-background-deep border border-border rounded px-2 py-1.5"
                    data-testid="2fa-secret-value">
                    {setupData.secret}
                  </div>
                )}
              </div>
              <div>
                <p className="text-sm text-slate-300 leading-relaxed">
                  Scan the QR code with your authenticator app, then enter the 6-digit code to
                  activate. If you can&apos;t scan, use the &quot;Show secret&quot; link to enter it manually.
                </p>
                <div className="mt-4">
                  <label htmlFor="2fa-code" className="block text-xs font-semibold uppercase tracking-[0.15em] text-slate-400 mb-2">
                    Verification code
                  </label>
                  <div className="flex items-center gap-3">
                    <OtpInput id="2fa-code" value={enrollCode} onChange={setEnrollCode} autoFocus />
                    <button onClick={enable}
                      disabled={busy || enrollCode.length !== 6}
                      data-testid="2fa-enable-btn"
                      className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-brand-cyan text-brand-navy hover:bg-brand-cyan/90 rounded-md font-semibold disabled:opacity-50 disabled:cursor-not-allowed">
                      <Lock size={14} weight="bold" />
                      {busy ? "Verifying…" : "Enable"}
                    </button>
                    <button onClick={() => setSetupData(null)} disabled={busy}
                      className="text-xs text-slate-500 hover:text-white">Cancel</button>
                  </div>
                </div>
              </div>
            </div>
          )
        ) : (
          // Enabled state
          <div className="space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div className="text-sm text-slate-300">
                2FA is <span className="text-emerald-400 font-semibold">active</span> on this account.
                {typeof status?.backup_codes_remaining === "number" && (
                  <> · <span className="text-slate-400" data-testid="2fa-backup-count">
                    {status.backup_codes_remaining} of 10 backup codes remaining
                  </span></>
                )}
              </div>
              <button onClick={() => setShowRegen(!showRegen)}
                data-testid="2fa-regen-toggle"
                className="text-[11px] font-semibold uppercase tracking-[0.15em] border border-border hover:border-brand-cyan/40 hover:bg-brand-cyan/10 rounded px-3 py-1.5 text-slate-300 hover:text-brand-cyan transition-colors">
                Regenerate backup codes
              </button>
            </div>

            {showRegen && (
              <div className="border border-border rounded p-3 flex items-center gap-3">
                <div className="text-xs text-slate-400">Enter your current TOTP code:</div>
                <OtpInput value={regenCode} onChange={setRegenCode} />
                <button onClick={regenerate} disabled={busy || regenCode.length !== 6}
                  data-testid="2fa-regen-btn"
                  className="text-xs px-3 py-1.5 bg-brand-cyan text-brand-navy rounded hover:bg-brand-cyan/90 disabled:opacity-50">
                  {busy ? "…" : "Regenerate"}
                </button>
                <button onClick={() => setShowRegen(false)} className="text-xs text-slate-500 hover:text-white">Cancel</button>
              </div>
            )}

            {showDisable && (
              <div className="border border-red-500/30 bg-red-500/5 rounded p-3 flex items-center gap-3"
                data-testid="2fa-disable-form">
                <div className="text-xs text-red-200">Enter a TOTP or backup code to confirm:</div>
                <input value={disableCode}
                  onChange={(e) => setDisableCode(e.target.value.trim())}
                  data-testid="2fa-disable-input"
                  placeholder="123456 or XXXXX-XXXXX"
                  className="text-sm font-mono bg-background-deep border border-border rounded px-3 py-1.5 text-slate-200 focus:outline-none focus:border-red-400" />
                <button onClick={disable} disabled={busy || disableCode.length < 6}
                  data-testid="2fa-disable-confirm-btn"
                  className="text-xs px-3 py-1.5 bg-red-500 text-white rounded hover:bg-red-600 disabled:opacity-50">
                  {busy ? "…" : "Confirm disable"}
                </button>
                <button onClick={() => setShowDisable(false)} className="text-xs text-slate-500 hover:text-white">Cancel</button>
              </div>
            )}
          </div>
        )}

        {backupCodes && (
          <div className="border border-amber-500/40 bg-amber-500/5 rounded p-4"
            data-testid="2fa-backup-codes">
            <div className="flex items-start gap-2 mb-3">
              <Warning size={16} weight="fill" className="text-amber-400 mt-0.5" />
              <div>
                <div className="text-sm font-semibold text-amber-200">
                  Save these backup codes now — this is the ONLY time they will be shown.
                </div>
                <div className="text-[12px] text-amber-200/80 mt-0.5">
                  Each code works exactly once when your authenticator device isn&apos;t available.
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {backupCodes.map((c, i) => (
                <button key={c} onClick={() => copyCode(c, i)}
                  data-testid={`2fa-backup-code-${i}`}
                  className="font-mono text-[13px] text-white bg-background-deep border border-border hover:border-brand-cyan/40 rounded px-2 py-1.5 flex items-center justify-between gap-1 transition-colors">
                  <span>{c}</span>
                  {copiedIdx === i
                    ? <Check size={12} weight="bold" className="text-emerald-400" />
                    : <Copy size={12} className="text-slate-500" />}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
